import React, { useState } from 'react';
import SimpleChart from './SimpleChart';
import {
  TrendingUp,
  TrendingDown,
  Users,
  ShoppingCart,
  DollarSign,
  Eye,
  Calendar,
  Download,
  Filter
} from 'lucide-react';

const AnalyticsCharts = ({ data }) => {
  const [timeRange, setTimeRange] = useState('6months');
  const [selectedMetric, setSelectedMetric] = useState('sales');

  // Extended analytics data
  const analyticsData = {
    pageViews: [
      { month: 'Jan', value: 12000 },
      { month: 'Feb', value: 15000 },
      { month: 'Mar', value: 18000 },
      { month: 'Apr', value: 16000 },
      { month: 'May', value: 22000 },
      { month: 'Jun', value: 25000 },
    ],
    userGrowth: [
      { month: 'Jan', value: 120 },
      { month: 'Feb', value: 135 },
      { month: 'Mar', value: 148 },
      { month: 'Apr', value: 156 },
      { month: 'May', value: 167 },
      { month: 'Jun', value: 180 },
    ],
    conversionRate: [
      { month: 'Jan', value: 2.4 },
      { month: 'Feb', value: 2.8 },
      { month: 'Mar', value: 3.1 },
      { month: 'Apr', value: 2.9 },
      { month: 'May', value: 3.4 },
      { month: 'Jun', value: 3.7 },
    ],
    topProducts: [
      { name: 'Arduino Uno', sales: 45, revenue: 2250 },
      { name: 'ESP32 Module', sales: 38, revenue: 1520 },
      { name: 'Sensor Kit', sales: 32, revenue: 960 },
      { name: 'Breadboard', sales: 28, revenue: 420 },
      { name: 'Jumper Wires', sales: 25, revenue: 250 },
    ]
  };

  const metrics = [
    {
      id: 'sales',
      label: 'Sales Revenue',
      data: data.salesData,
      icon: DollarSign,
      color: 'blue',
      format: (value) => `$${value.toLocaleString()}`
    },
    {
      id: 'pageviews',
      label: 'Page Views',
      data: analyticsData.pageViews,
      icon: Eye,
      color: 'green',
      format: (value) => value.toLocaleString()
    },
    {
      id: 'users',
      label: 'User Growth',
      data: analyticsData.userGrowth,
      icon: Users,
      color: 'purple',
      format: (value) => value.toLocaleString()
    },
    {
      id: 'conversion',
      label: 'Conversion Rate',
      data: analyticsData.conversionRate,
      icon: TrendingUp,
      color: 'yellow',
      format: (value) => `${value}%`
    }
  ];

  const currentMetric = metrics.find(m => m.id === selectedMetric) || metrics[0];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics & Reports</h1>
          <p className="text-gray-600">Detailed insights into your store performance</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="1month">Last Month</option>
            <option value="3months">Last 3 Months</option>
            <option value="6months">Last 6 Months</option>
            <option value="1year">Last Year</option>
          </select>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center">
            <Download size={16} className="mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Metric Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Metric</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {metrics.map((metric) => {
            const Icon = metric.icon;
            const isSelected = selectedMetric === metric.id;
            
            return (
              <button
                key={metric.id}
                onClick={() => setSelectedMetric(metric.id)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Icon size={24} className={`mb-2 ${
                  isSelected ? 'text-blue-600' : 'text-gray-500'
                }`} />
                <p className={`font-medium ${
                  isSelected ? 'text-blue-900' : 'text-gray-900'
                }`}>
                  {metric.label}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">{currentMetric.label} Trend</h3>
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded hover:bg-gray-100">
              <Filter size={16} className="text-gray-500" />
            </button>
          </div>
        </div>
        <div className="h-80">
          <SimpleChart data={currentMetric.data} type="line" height={320} />
        </div>
      </div>

      {/* Secondary Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Performance */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Performance</h3>
          <SimpleChart data={data.categoryData} type="doughnut" height={250} />
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Products</h3>
          <div className="space-y-4">
            {analyticsData.topProducts.map((product, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{product.name}</p>
                    <p className="text-sm text-gray-500">{product.sales} sales</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">${product.revenue}</p>
                  <div className="w-20 bg-gray-200 rounded-full h-2 mt-1">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${(product.sales / 50) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">Conversion Rate</h4>
            <TrendingUp size={20} className="text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">3.7%</p>
          <p className="text-sm text-green-600">+0.3% from last month</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">Avg. Order Value</h4>
            <DollarSign size={20} className="text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">$89</p>
          <p className="text-sm text-blue-600">+$12 from last month</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">Return Rate</h4>
            <TrendingDown size={20} className="text-red-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">2.1%</p>
          <p className="text-sm text-green-600">-0.5% from last month</p>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsCharts;
